import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import classnames from 'classnames';
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import defaultTo from 'lodash/defaultTo';
import kebabCase from 'lodash/kebabCase';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { AnalyticsEventSource, trackLinkClick, trackSearchEvent } from '@atlassian/temp-analytics';
import { getEntitlementCountForTransactionAccount } from '@atlassian/temp-proxies';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { QuoteV2 } from '@atlassian/cssxp-types-quote';
import { AppContent, AppPage, useApplicationContext, useFeatureToggle, useRecentlyViewed } from '@css-xp/base';
import { PPC_PAGE_TITLE, RecentlyViewedEntityEnum, routePaths, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { getEntityName, getRouteUrl } from '@css-xp/ppc-utils';
import {
  PartnerSearchContext,
  RecentlyViewedCloudSiteMetadata,
  RecentlyViewedCustomerAddressMetadata,
  RecentlyViewedSubscriptionMetadata,
} from '@css-xp/ppc-types';
// @ts-ignore
import {
  SEARCH_CONTEXT_ALL,
  SearchContextContainer,
  SearchContextSelectItem,
  SearchContextsMap,
  SearchData,
  searchFieldKey,
  SearchForm,
  SearchInputContainer,
} from '@css-xp/ppc-search-form';
import { SortDirection, InvoiceStatus } from '@css-xp/graphtypes/src/generated-types-new';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { HomePageLoader, HomePageBoxLoader, TableLoader } from '@css-xp/ppc-page-loaders';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { ScreenEventAnalytics, Greeting } from '@css-xp/ppc-common';
import { HeaderActions } from './components/header-actions';
import { UserInfo } from './components/user-info';
import { getOverdueInvoicesWidgetContent, OverdueInvoicesQueryWrapper } from './components/overdue-invoices';
import { CustomerAddressQueryWrapper, getCustomerAddressWidgetContent } from './components/customer-address';
import { Widget, WidgetContainer } from './components/widget';
import { getUpcomingRenewalWidgetContent, UpcomingRenewalsQueryWrapper } from './components/upcoming-renewals';
import {
  getRecentlyViewedSubscriptionWidgetContent,
  RecentlyViewedSubscriptionsQueryWrapper,
} from './components/recent-subscriptions';
import {
  getRecentGeneratedInvoicesWidgetContent,
  RecentlyGeneratedInvoicesQueryWrapper,
} from './components/recent-invoices';
import {
  getRecentlyViewedCloudSiteWidgetContent,
  RecentlyViewedCloudSitesQueryWrapper,
} from './components/recent-cloud-sites';
import {
  getSubscriptionCreatedWidgetContent,
  SubscriptionCreatedQueryWrapper,
} from './components/subscription-created';
import { getRecentGeneratedQuotesWidgetContent, RecentlyGeneratedQuotesQueryWrapper } from './components/recent-quotes';
import { DueQuotesQueryWrapper, getDueQuotesWidgetContent } from './components/due-quotes';
import EmptyDashboardIcon from './assets/empty-dashboard.svg';
import './home-page.scss';
import { ButtonTabContainer, HomePageWidgetContainer } from './home-page.styles';

enum WidgetHeading {
  RECENTLY_VIEWED_SUBSCRIPTIONS = 'Recently viewed subscriptions',
  RECENTLY_CREATED_SUBSCRIPTIONS = 'Recently created subscriptions',
  RECENTLY_VIEWED_CLOUD_SITES = 'Recently viewed cloud sites',
  OVERDUE_INVOICES = 'Overdue invoices',
  RECENTLY_GENERATED_INVOICES = 'Recently generated invoices',
  UPCOMING_RENEWALS = 'Upcoming renewals',
  RECENTLY_VIEWED_CUSTOMER_ADDRESSES = 'Recently viewed customer addresses',
  RECENTLY_GENERATED_QUOTES = 'Recently generated quotes',
  QUOTES_EXPIRING_SOON = 'Quotes expiring soon',
  MY_QUOTES = 'My quotes',
  MY_INVOICES = 'My invoices',
  SUBSCRIPTIONS = 'Subscriptions',
  RECENTLY_VIEWED = 'Recently viewed',
}

interface EntitlementCountProps {
  count: number | null;
  loading: boolean;
  error: any;
}

const getViewAllUrlBasedOnSortAndFilters = (
  url: string,
  sortKey: string,
  sortDirection: SortDirection,
  filters: string | null = null
) => `${url}?sort=${sortKey}&order=${sortDirection}&filters=${filters}`;

const boxStyles = xcss({
  borderStyle: 'solid',
  borderRadius: 'border.radius.200',
  borderWidth: 'border.width',
  borderColor: 'color.border.disabled',
  marginBottom: 'space.200',
  width: '100%',
});

const recentlyGeneratedText = 'Recently generated';
const expiringSoonText = 'Expiring soon';
const overdueText = 'Overdue';
const recentlyCreatedText = 'Recently created';
const renewSoonText = 'Renew soon';
const SubscriptionsText = 'Subscriptions';
const CustomersText = 'Customers';
const CloudSitesText = 'Cloud sites';

const renderWidgetBox = (
  heading: React.ReactNode,
  children: React.ReactNode,
  viewAll: React.ReactNode,
  subHeading?: React.ReactNode
) => {
  return (
    <Box padding="space.300" xcss={boxStyles}>
      <Inline alignBlock="center" spread="space-between">
        <Inline space="space.100">
          <h3 className="widget-heading">{heading}</h3>
          {subHeading && <p className="widget-content-sub-heading">{subHeading}</p>}
        </Inline>
        {viewAll}
      </Inline>
      {children}
    </Box>
  );
};

const renderButtonTab = (
  tabName: string,
  tabNameKey: string,
  selectedTabKey: string,
  setSelectedTabKey: (a: string) => void
) => {
  return (
    <Button
      className={classnames('button-tab', {
        'button-tab-active': selectedTabKey === tabNameKey,
      })}
      onClick={() => {
        setSelectedTabKey(tabNameKey);
      }}
    >
      {tabName}
    </Button>
  );
};

const QuotesWidget = ({
  renderViewAll,
  renderGeneratedQuotes,
  renderDueQuotes,
}: {
  renderViewAll: () => React.ReactNode;
  renderGeneratedQuotes: () => React.ReactNode;
  renderDueQuotes: () => React.ReactNode;
}) => {
  const [selectedTabKey, setSelectedTabKey] = useState<string>(kebabCase(recentlyGeneratedText));
  const viewAll = renderViewAll();
  const heading = WidgetHeading.MY_QUOTES;
  const subHeading = <Lozenge appearance="inprogress">{QuoteV2.QuoteStatus.OPEN}</Lozenge>;

  const children = (
    <div className="quotes-widget">
      <ButtonTabContainer>
        {renderButtonTab(recentlyGeneratedText, kebabCase(recentlyGeneratedText), selectedTabKey, setSelectedTabKey)}
        {renderButtonTab(expiringSoonText, kebabCase(expiringSoonText), selectedTabKey, setSelectedTabKey)}
      </ButtonTabContainer>
      {selectedTabKey === kebabCase(recentlyGeneratedText) ? renderGeneratedQuotes() : renderDueQuotes()}
    </div>
  );

  return renderWidgetBox(heading, children, viewAll, subHeading);
};

const InvoicesWidget = ({
  renderViewAll,
  renderGeneratedInvoices,
  renderOverdueInvoices,
}: {
  renderViewAll: () => React.ReactNode;
  renderGeneratedInvoices: () => React.ReactNode;
  renderOverdueInvoices: () => React.ReactNode;
}) => {
  const [selectedTabKey, setSelectedTabKey] = useState<string>(kebabCase(recentlyGeneratedText));

  const renderButtonTab = (tabName: string, tabNameKey: string) => {
    // Note: Common function with the same name is not used due `button-tab-warning` class logic
    return (
      <Button
        className={classnames('button-tab', {
          'button-tab-active': selectedTabKey === tabNameKey,
          'button-tab-warning': selectedTabKey !== kebabCase(overdueText),
        })}
        onClick={() => {
          setSelectedTabKey(tabNameKey);
        }}
      >
        {tabName}
      </Button>
    );
  };

  const viewAll = renderViewAll();
  const heading = WidgetHeading.MY_INVOICES;
  const children = (
    <div className="invoices-widget">
      <ButtonTabContainer>
        {renderButtonTab(recentlyGeneratedText, kebabCase(recentlyGeneratedText))}
        {renderButtonTab(overdueText, kebabCase(overdueText))}
      </ButtonTabContainer>
      {selectedTabKey === kebabCase(recentlyGeneratedText) ? renderGeneratedInvoices() : renderOverdueInvoices()}
    </div>
  );

  return renderWidgetBox(heading, children, viewAll);
};

const SubscriptionsWidget = ({
  renderViewAll,
  renderRecentlyCreated,
  renderUpcomingRenewals,
}: {
  renderViewAll: () => React.ReactNode;
  renderRecentlyCreated: () => React.ReactNode;
  renderUpcomingRenewals: () => React.ReactNode;
}) => {
  const [selectedTabKey, setSelectedTabKey] = useState<string>(kebabCase(recentlyCreatedText));
  const viewAll = renderViewAll();
  const heading = WidgetHeading.SUBSCRIPTIONS;

  const children = (
    <>
      <ButtonTabContainer>
        {renderButtonTab(recentlyCreatedText, kebabCase(recentlyCreatedText), selectedTabKey, setSelectedTabKey)}
        {renderButtonTab(renewSoonText, kebabCase(renewSoonText), selectedTabKey, setSelectedTabKey)}
      </ButtonTabContainer>
      {selectedTabKey === kebabCase(recentlyCreatedText) ? renderRecentlyCreated() : renderUpcomingRenewals()}
    </>
  );

  return renderWidgetBox(heading, children, viewAll);
};

const RecentlyViewedWidget = ({
  renderSubscriptions,
  renderCloudSites,
  renderCustomers,
}: {
  renderSubscriptions: () => React.ReactNode;
  renderCloudSites: () => React.ReactNode;
  renderCustomers: () => React.ReactNode;
}) => {
  const [selectedTabKey, setSelectedTabKey] = useState<string>(kebabCase(SubscriptionsText));
  const renderTabContent = () => {
    switch (selectedTabKey) {
      case kebabCase(SubscriptionsText):
        return renderSubscriptions();
      case kebabCase(CustomersText):
        return renderCustomers();
      case kebabCase(CloudSitesText):
        return renderCloudSites();
      default:
        return null;
    }
  };

  const heading = WidgetHeading.RECENTLY_VIEWED;

  const children = (
    <div className="recently-viewed-widget">
      <ButtonTabContainer>
        {renderButtonTab(SubscriptionsText, kebabCase(SubscriptionsText), selectedTabKey, setSelectedTabKey)}
        {renderButtonTab(CustomersText, kebabCase(CustomersText), selectedTabKey, setSelectedTabKey)}
        {renderButtonTab(CloudSitesText, kebabCase(CloudSitesText), selectedTabKey, setSelectedTabKey)}
      </ButtonTabContainer>
      {renderTabContent()}
    </div>
  );

  return renderWidgetBox(heading, children, '');
};

const recentlyViewedWidgetCommonProps = {
  initialItemsCount: 7,
  widgetItemClassName: 'widget-item-recently-viewed',
  loader: (
    <>
      <br />
      <TableLoader hideHeader hideActions hideFilters hideSearchBar shouldRightAlignLastCol columnsCount={2} />
    </>
  ),
};

export const HomePage = () => {
  const { txaId } = useParams<{
    txaId: string;
  }>();
  useTitle(PPC_PAGE_TITLE.HOME);
  const user = useApplicationContext();

  const currentPartnerAccount = user?.partnerAccounts?.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );
  const hasPayOnAccount = defaultTo(currentPartnerAccount?.payOnAccount, false);

  const { getRecentlyViewed } = useRecentlyViewed();
  const history = useHistory();
  const [searchInputPlaceHolder, setSearchInputPlaceHolder] = useState<string | undefined>(
    SearchContextsMap.get(PartnerSearchContext.ALL)?.placeHolderText
  );
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const invoiceListUrl = useMemo<string>(() => getRouteUrl(routePaths.invoices, { txaId }), [txaId]);
  const subscriptionListUrl = useMemo<string>(() => getRouteUrl(routePaths.subscriptions, { txaId }), [txaId]);
  const customerListUrl = useMemo<string>(() => getRouteUrl(routePaths.customerAddresses, { txaId }), [txaId]);
  const quoteListUrl = useMemo<string>(() => getRouteUrl(routePaths.quotes, { txaId }), [txaId]);
  const [entitlementCountData, setEntitlementCountData] = useState<Partial<EntitlementCountProps>>({
    count: null,
    loading: true,
    error: null,
  });

  const fetchEntitlementCountData = useCallback(async () => {
    setEntitlementCountData({ count: null, loading: true, error: null });
    try {
      const res = await getEntitlementCountForTransactionAccount(txaId);
      if (res) {
        setEntitlementCountData({ count: res.count, loading: false, error: null });
      }
    } catch (e) {
      setEntitlementCountData({ count: null, loading: false, error: e });
    }
  }, [txaId]);

  useEffect(() => {
    fetchEntitlementCountData();
  }, [fetchEntitlementCountData]);

  const showWelcomeMessage = entitlementCountData.count === 0;
  const loading = entitlementCountData.loading;

  const onSearchSubmit = (data: SearchData) => {
    const query = encodeURIComponent(data[searchFieldKey]);
    history.push(
      `${getRouteUrl(routePaths.search, { txaId })}?${searchFieldKey}=${query}&context=${
        data.context || SEARCH_CONTEXT_ALL.value
      }`
    );
    trackSearchEvent({
      selectedContext: data?.context ? data.context : SEARCH_CONTEXT_ALL.value,
    });
  };

  const onSearchContextSelected = (context: SearchContextSelectItem) => {
    setSearchInputPlaceHolder(context.placeHolderText);
  };

  const renderViewAll = useCallback(
    (url: string, heading?: string, classNames?: string) => {
      return (
        <div className={classNames ?? 'pv-20 ph-20'} data-testid="view-all">
          <Link
            to={url}
            onClick={() => {
              trackLinkClick('viewAllClick', { source: heading });
            }}
          >
            View all
          </Link>
        </div>
      );
    },
    [txaId]
  );

  const renderRecentlyViewedSubscriptionWidget = () => {
    // RECENTLY VIEWED SUBSCRIPTIONS
    const subscriptionsData = getRecentlyViewed<RecentlyViewedSubscriptionMetadata>(
      getEntityName(RecentlyViewedEntityEnum.SUBSCRIPTION, txaId)
    );
    const heading = WidgetHeading.RECENTLY_VIEWED_SUBSCRIPTIONS;
    const viewAll = renderViewAll(subscriptionListUrl, heading);

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <RecentlyViewedSubscriptionsQueryWrapper
            txaId={txaId}
            recentlyViewedData={subscriptionsData.recentlyVisitedData}
          >
            {({ data, loading, error }) => (
              <Widget loading={loading} error={error} version={2} {...recentlyViewedWidgetCommonProps}>
                {getRecentlyViewedSubscriptionWidgetContent(data, heading, 2)}
              </Widget>
            )}
          </RecentlyViewedSubscriptionsQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <RecentlyViewedSubscriptionsQueryWrapper txaId={txaId} recentlyViewedData={subscriptionsData.recentlyVisitedData}>
        {({ data, loading, error }) => (
          <Widget heading={heading} initialItemsCount={6} loading={loading} headingAfter={viewAll} error={error}>
            {getRecentlyViewedSubscriptionWidgetContent(data, heading)}
          </Widget>
        )}
      </RecentlyViewedSubscriptionsQueryWrapper>
    );
  };

  const renderRecentlyCreatedSubscriptionWidget = () => {
    const heading = WidgetHeading.RECENTLY_CREATED_SUBSCRIPTIONS;
    const viewAll = renderViewAll(
      getViewAllUrlBasedOnSortAndFilters(subscriptionListUrl, 'createdDate', SortDirection.DESC, null),
      heading
    );

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <SubscriptionCreatedQueryWrapper txaId={txaId}>
            {({ data, loading, error }) => (
              <Widget
                initialItemsCount={6}
                testId="recently-created-subscriptions"
                loading={loading}
                error={error}
                loader={
                  <>
                    <br />
                    <TableLoader hideActions hideFilters hideSearchBar columnsCount={4} />
                  </>
                }
                version={2}
              >
                {getSubscriptionCreatedWidgetContent(
                  data?.transactionAccount?.productSubscriptionsInPPC?.nodes,
                  heading,
                  2
                )}
              </Widget>
            )}
          </SubscriptionCreatedQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <SubscriptionCreatedQueryWrapper txaId={txaId}>
        {({ data, loading, error }) => (
          <Widget heading={heading} initialItemsCount={6} loading={loading} headingAfter={viewAll} error={error}>
            {getSubscriptionCreatedWidgetContent(data?.transactionAccount?.productSubscriptionsInPPC?.nodes, heading)}
          </Widget>
        )}
      </SubscriptionCreatedQueryWrapper>
    );
  };

  const renderCloudSitesWidget = () => {
    // RECENTLY VIEWED CLOUD SITES
    const cloudSitesData = getRecentlyViewed<RecentlyViewedCloudSiteMetadata>(
      getEntityName(RecentlyViewedEntityEnum.CLOUD_SITE, txaId)
    );
    const heading = WidgetHeading.RECENTLY_VIEWED_CLOUD_SITES;

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <RecentlyViewedCloudSitesQueryWrapper txaId={txaId} recentlyViewedData={cloudSitesData.recentlyVisitedData}>
            {({ data, loading, error }) => (
              <Widget loading={loading} error={error} version={2} {...recentlyViewedWidgetCommonProps}>
                {getRecentlyViewedCloudSiteWidgetContent(data, heading, 2)}
              </Widget>
            )}
          </RecentlyViewedCloudSitesQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <RecentlyViewedCloudSitesQueryWrapper txaId={txaId} recentlyViewedData={cloudSitesData.recentlyVisitedData}>
        {({ data, loading, error }) => (
          <Widget heading={heading} initialItemsCount={6} loading={loading} error={error}>
            {getRecentlyViewedCloudSiteWidgetContent(data, heading)}
          </Widget>
        )}
      </RecentlyViewedCloudSitesQueryWrapper>
    );
  };

  const renderOverdueInvoices = () => {
    const heading = WidgetHeading.OVERDUE_INVOICES;
    const viewAll = renderViewAll(
      getViewAllUrlBasedOnSortAndFilters(
        invoiceListUrl,
        'dueDateIsLte',
        SortDirection.ASC,
        JSON.stringify([
          { key: 'status', value: InvoiceStatus.OPEN },
          { key: 'dueDate', value: 'PAST' },
          { key: 'hasPayOnAccount', value: hasPayOnAccount },
        ])
      ),
      heading
    );

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <OverdueInvoicesQueryWrapper txaId={txaId} hasPayOnAccount={hasPayOnAccount}>
            {({ data, loading, error }) => (
              <Widget
                initialItemsCount={6}
                testId="overdue-invoices"
                loading={loading}
                error={error}
                loader={<HomePageBoxLoader />}
                widgetItemClassName="widget-item-danger"
                version={2}
              >
                {getOverdueInvoicesWidgetContent(data?.transactionAccount?.overdueInvoices?.nodes, heading, 2)}
              </Widget>
            )}
          </OverdueInvoicesQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <OverdueInvoicesQueryWrapper txaId={txaId} hasPayOnAccount={hasPayOnAccount}>
        {({ data, loading, error }) => (
          <Widget
            testId="overdue-invoices"
            heading={heading}
            initialItemsCount={6}
            headingAfter={viewAll}
            loading={loading}
            error={error}
          >
            {getOverdueInvoicesWidgetContent(data?.transactionAccount?.overdueInvoices?.nodes, heading)}
          </Widget>
        )}
      </OverdueInvoicesQueryWrapper>
    );
  };

  const renderRecentlyGeneratedInvoices = () => {
    const heading = WidgetHeading.RECENTLY_GENERATED_INVOICES;
    const viewAll = renderViewAll(
      getViewAllUrlBasedOnSortAndFilters(invoiceListUrl, 'createdDate', SortDirection.DESC, null),
      heading
    );

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <RecentlyGeneratedInvoicesQueryWrapper txaId={txaId}>
            {({ data, loading, error }) => (
              <Widget
                initialItemsCount={6}
                testId="recent-invoices"
                loading={loading}
                error={error}
                loader={<HomePageBoxLoader />}
                version={2}
              >
                {getRecentGeneratedInvoicesWidgetContent(data?.transactionAccount?.invoices?.nodes, heading, 2)}
              </Widget>
            )}
          </RecentlyGeneratedInvoicesQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <RecentlyGeneratedInvoicesQueryWrapper txaId={txaId}>
        {({ data, loading, error }) => (
          <Widget
            heading={heading}
            initialItemsCount={6}
            testId="recent-invoices"
            loading={loading}
            headingAfter={viewAll}
            error={error}
          >
            {getRecentGeneratedInvoicesWidgetContent(data?.transactionAccount?.invoices?.nodes, heading)}
          </Widget>
        )}
      </RecentlyGeneratedInvoicesQueryWrapper>
    );
  };

  const renderUpcomingRenewals = () => {
    const heading = WidgetHeading.UPCOMING_RENEWALS;
    // framing the filters array as per the upcoming renewals widget query params
    const filters = [
      {
        key: 'lastPeriodEndDateIsGte',
        // fetching only the subscriptions having subscription period more than today
        value: startOfDay(new Date()).toISOString(),
      },
      {
        key: 'lastPeriodEndDateIsLte',
        // fetching only the subscriptions of last 90 days subscription period
        value: endOfDay(addDays(new Date(), 90)).toISOString(),
      },
    ];
    const viewAll = renderViewAll(
      getViewAllUrlBasedOnSortAndFilters(
        subscriptionListUrl,
        'lastPeriodEndDate',
        SortDirection.ASC,
        JSON.stringify(filters)
      ),
      heading
    );

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <UpcomingRenewalsQueryWrapper txaId={txaId}>
            {({ data, loading, error }) => (
              <Widget
                initialItemsCount={6}
                testId="renew-soon-subscriptions"
                loading={loading}
                error={error}
                loader={
                  <>
                    <br />
                    <TableLoader hideActions hideFilters hideSearchBar columnsCount={4} />
                  </>
                }
                version={2}
              >
                {getUpcomingRenewalWidgetContent(
                  data?.transactionAccount?.productSubscriptionsInPPC?.nodes,
                  loading,
                  heading,
                  2
                )}
              </Widget>
            )}
          </UpcomingRenewalsQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <UpcomingRenewalsQueryWrapper txaId={txaId}>
        {({ data, loading, error }) => (
          <Widget heading={heading} initialItemsCount={6} headingAfter={viewAll} error={error}>
            {getUpcomingRenewalWidgetContent(
              data?.transactionAccount?.productSubscriptionsInPPC?.nodes,
              loading,
              heading
            )}
          </Widget>
        )}
      </UpcomingRenewalsQueryWrapper>
    );
  };

  const renderCustomerAddressWidget = () => {
    // RECENTLY VIEWED CUSTOMERS
    const customerAddressesData = getRecentlyViewed<RecentlyViewedCustomerAddressMetadata>(
      getEntityName(RecentlyViewedEntityEnum.CUSTOMER_ADDRESS, txaId)
    );
    if (customerAddressesData.hasError) {
      return null;
    }

    const heading = WidgetHeading.RECENTLY_VIEWED_CUSTOMER_ADDRESSES;
    const viewAll = renderViewAll(customerListUrl, heading);

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <CustomerAddressQueryWrapper txaId={txaId} recentlyViewedData={customerAddressesData.recentlyVisitedData}>
            {({ data, loading, error }) => (
              <Widget loading={loading} error={error} version={2} {...recentlyViewedWidgetCommonProps}>
                {getCustomerAddressWidgetContent(data, heading, 2)}
              </Widget>
            )}
          </CustomerAddressQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <CustomerAddressQueryWrapper txaId={txaId} recentlyViewedData={customerAddressesData.recentlyVisitedData}>
        {({ data, loading, error }) => (
          <Widget heading={heading} initialItemsCount={6} loading={loading} headingAfter={viewAll} error={error}>
            {getCustomerAddressWidgetContent(data, heading)}
          </Widget>
        )}
      </CustomerAddressQueryWrapper>
    );
  };

  const renderGeneratedQuotes = () => {
    const heading = WidgetHeading.RECENTLY_GENERATED_QUOTES;
    const viewAll = renderViewAll(quoteListUrl, heading);
    const subHeading = <Lozenge appearance="inprogress">{QuoteV2.QuoteStatus.OPEN}</Lozenge>;

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <RecentlyGeneratedQuotesQueryWrapper txaId={txaId}>
            {({ data, loading, error }) => (
              <Widget
                testId="recent-quotes"
                initialItemsCount={6}
                loading={loading}
                error={error}
                subHeading={undefined}
                loader={<HomePageBoxLoader />}
                version={2}
              >
                {getRecentGeneratedQuotesWidgetContent(data?.quotes?.nodes, heading, 2)}
              </Widget>
            )}
          </RecentlyGeneratedQuotesQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <RecentlyGeneratedQuotesQueryWrapper txaId={txaId}>
        {({ data, loading, error }) => (
          <Widget
            testId="recent-quotes"
            heading={heading}
            initialItemsCount={6}
            headingAfter={viewAll}
            loading={loading}
            error={error}
            subHeading={subHeading}
          >
            {getRecentGeneratedQuotesWidgetContent(data?.quotes?.nodes, heading)}
          </Widget>
        )}
      </RecentlyGeneratedQuotesQueryWrapper>
    );
  };

  const renderDueQuotes = () => {
    const heading = WidgetHeading.QUOTES_EXPIRING_SOON;
    const viewAll = renderViewAll(quoteListUrl, heading);
    const subHeading = <Lozenge appearance="inprogress">{QuoteV2.QuoteStatus.OPEN}</Lozenge>;

    if (isDesignIteration1Enabled) {
      return (
        <HomePageWidgetContainer>
          <DueQuotesQueryWrapper txaId={txaId}>
            {({ data, loading, error }) => (
              <Widget
                testId="due-quotes"
                initialItemsCount={6}
                loading={loading}
                error={error}
                subHeading={undefined}
                loader={<HomePageBoxLoader />}
                version={2}
              >
                {getDueQuotesWidgetContent(data?.quotes?.nodes, heading, 2)}
              </Widget>
            )}
          </DueQuotesQueryWrapper>
        </HomePageWidgetContainer>
      );
    }

    return (
      <DueQuotesQueryWrapper txaId={txaId}>
        {({ data, loading, error }) => (
          <Widget
            testId="due-quotes"
            heading={heading}
            initialItemsCount={6}
            headingAfter={viewAll}
            loading={loading}
            error={error}
            subHeading={subHeading}
          >
            {getDueQuotesWidgetContent(data?.quotes?.nodes, heading)}
          </Widget>
        )}
      </DueQuotesQueryWrapper>
    );
  };

  const renderWelcomeMessage = () => {
    if (isDesignIteration1Enabled) {
      const welcomeMessageContainerStyles = xcss({
        flex: '1',
      });

      return (
        <Flex justifyContent="center" alignItems="center" xcss={welcomeMessageContainerStyles}>
          <Stack space="space.600" alignInline="center">
            {/* @ts-ignore */}
            <img src={EmptyDashboardIcon} alt="icon" width="408" height="100%" />
            <Stack space="space.300" alignInline="center">
              <Heading size="xlarge">No information yet. Let&apos;s change that</Heading>
              <Text align="center" size="large" as="p">
                Here you&apos;ll see an overview of your billing activities across the portal. Add a customer or any
                billing details to get started.
              </Text>
            </Stack>
          </Stack>
        </Flex>
      );
    }

    return (
      <p className="ppc-welcome-message">
        <h1 className="mb-32">Welcome to Partner Purchasing Center 👋</h1>
        <p className="mb-16">
          Want to add a customer or payment method, explore the catalog, or buy a new cloud product? There are a few
          ways to get started.
        </p>
        {/*<p className="mb-16">Our online resources are also here to guide you through.</p>*/}
      </p>
    );
  };
  const renderHeader = () => {
    if (isDesignIteration1Enabled) {
      if (loading) {
        return <HomePageLoader hideBody />;
      }
      const breadcrumbsItems = [
        {
          title: 'Home',
          href: routePaths.home,
        },
      ];
      const heading = (
        <>
          <Greeting /> {user.displayName}
        </>
      );

      return <PageBackgroundV2 heading={heading} breadcrumbsProps={{ items: breadcrumbsItems }} />;
    }

    return (
      <div className="container">
        <div className="home-header mt-50">
          <UserInfo user={user} />
          <HeaderActions transactionAccountId={txaId} hideNewCustomerAction={true} />
        </div>
      </div>
    );
  };

  const renderSearchForm = () => {
    if (isDesignIteration1Enabled) {
      return null;
    }

    return (
      <div className="container">
        <SearchForm onSubmit={onSearchSubmit}>
          {() => (
            <>
              <SearchContextContainer defaultValue={SEARCH_CONTEXT_ALL.value} onChange={onSearchContextSelected} />
              <SearchInputContainer isClearable placeHolderText={searchInputPlaceHolder} />
            </>
          )}
          {/*<DarkButton appearance="primary" className="ml-8 search-btn" type="submit">*/}
          {/*  Submit*/}
          {/*</DarkButton>*/}
        </SearchForm>
      </div>
    );
  };
  const renderLoader = () => {
    if (isDesignIteration1Enabled) {
      return <HomePageLoader hideHeader />;
    }

    return <LoadingIndicator />;
  };

  const renderWidgets = () => {
    if (isDesignIteration1Enabled) {
      return (
        <Stack space="space.100">
          <Inline space="space.300" grow="fill">
            <QuotesWidget
              renderViewAll={() => renderViewAll(quoteListUrl, WidgetHeading.MY_QUOTES, '')}
              renderGeneratedQuotes={renderGeneratedQuotes}
              renderDueQuotes={renderDueQuotes}
            />
            <InvoicesWidget
              renderViewAll={() => renderViewAll(invoiceListUrl, WidgetHeading.MY_INVOICES, '')}
              renderGeneratedInvoices={renderRecentlyGeneratedInvoices}
              renderOverdueInvoices={renderOverdueInvoices}
            />
          </Inline>
          <SubscriptionsWidget
            renderViewAll={() => renderViewAll(subscriptionListUrl, WidgetHeading.SUBSCRIPTIONS, '')}
            renderRecentlyCreated={renderRecentlyCreatedSubscriptionWidget}
            renderUpcomingRenewals={renderUpcomingRenewals}
          />
          <RecentlyViewedWidget
            renderSubscriptions={renderRecentlyViewedSubscriptionWidget}
            renderCustomers={renderCustomerAddressWidget}
            renderCloudSites={renderCloudSitesWidget}
          />
        </Stack>
      );
    }

    return (
      <>
        <WidgetContainer heading="Upcoming and urgent">
          {renderOverdueInvoices()}
          {renderUpcomingRenewals()}
          {renderRecentlyGeneratedInvoices()}
          {renderRecentlyCreatedSubscriptionWidget()}
        </WidgetContainer>
        <WidgetContainer heading="My quotes">
          {renderGeneratedQuotes()}
          {renderDueQuotes()}
        </WidgetContainer>
        <WidgetContainer heading="Your recent activity">
          {renderRecentlyViewedSubscriptionWidget()}
          {renderCustomerAddressWidget()}
          {renderCloudSitesWidget()}
        </WidgetContainer>
      </>
    );
  };

  const renderBody = () => {
    return showWelcomeMessage ? renderWelcomeMessage() : renderWidgets();
  };

  return (
    <AppPage
      className={classnames('app-page-v2 ppc-home-page', {
        'background-cover': showWelcomeMessage || loading,
      })}
    >
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.PARTNER_HOME_SCREEN,
          attributes: {},
        }}
      />
      {renderHeader()}
      {renderSearchForm()}
      <AppContent
        className={classnames('ppc-home-page-content', {
          'gray-bg ': !isDesignIteration1Enabled,
          'ppc-home-page-content-v2': isDesignIteration1Enabled,
        })}
      >
        {loading ? renderLoader() : renderBody()}
      </AppContent>
    </AppPage>
  );
};
